import { createClient } from '@supabase/supabase-js'

// You'll need to replace these with your actual Supabase project values
// Get these from your Supabase project dashboard
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'your_supabase_project_url'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your_supabase_anon_key'

// Check if we have valid configuration before creating the client
export const isSupabaseConfigured = () => {
  return supabaseUrl !== 'your_supabase_project_url' && 
         supabaseAnonKey !== 'your_supabase_anon_key' &&
         supabaseUrl.startsWith('http') &&
         supabaseAnonKey.length > 0
}

// Create Supabase client only if properly configured
let supabase = null

try {
  if (isSupabaseConfigured()) {
    supabase = createClient(supabaseUrl, supabaseAnonKey)
  }
} catch (error) {
  console.warn('Failed to initialize Supabase client:', error.message)
  supabase = null
}

// Export a getter function to safely access the client
export const getSupabaseClient = () => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }
  if (!supabase) {
    throw new Error('Supabase client not initialized')
  }
  return supabase
}

// For backward compatibility, export supabase but with error handling
export { supabase }

// Database health check function
export const checkDatabaseHealth = async () => {
  if (!isSupabaseConfigured()) {
    return { 
      isHealthy: false, 
      error: 'Database not configured' 
    }
  }

  try {
    // Test basic connectivity with auth service (always available)
    const client = getSupabaseClient()
    const { error } = await client.auth.getSession()
    
    // If we get an error, check if it's a connectivity issue
    if (error) {
      // Common connectivity error codes
      const connectivityErrors = [
        'PGRST301', // Connection refused
        'PGRST302', // Connection timeout
        'PGRST303', // Network error
        '08006',    // Connection failure
        '08001',    // SQL client unable to establish SQL connection
        '08003',    // Connection does not exist
        '08004',    // SQL server rejected establishment of SQL connection
        '08007',    // Transaction resolution unknown
        '08P01',    // Protocol violation
        'ENOTFOUND', // DNS resolution failed
        'ECONNREFUSED', // Connection refused
        'ETIMEDOUT', // Connection timeout
        'ENETUNREACH', // Network unreachable
      ]
      
      const isConnectivityError = connectivityErrors.some(code => 
        error.message?.includes(code) || error.code === code
      )
      
      if (isConnectivityError) {
        return { 
          isHealthy: false, 
          error: 'Database connection failed',
          details: error.message 
        }
      }
      
      // For other errors (like RLS policies), consider it healthy
      // as the database is reachable but just has permission issues
      return { 
        isHealthy: true, 
        error: null 
      }
    }
    
    return { 
      isHealthy: true, 
      error: null 
    }
  } catch (err) {
    // Network or other errors
    return { 
      isHealthy: false, 
      error: 'Database health check failed',
      details: err.message 
    }
  }
}

// Test database connectivity with retry mechanism
export const testDatabaseConnection = async (maxRetries = 3, delay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const health = await checkDatabaseHealth()
    
    if (health.isHealthy) {
      return { success: true, attempt }
    }
    
    // If this is not the last attempt, wait before retrying
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, delay * attempt))
    }
  }
  
  return { 
    success: false, 
    error: 'Database connection failed after multiple attempts' 
  }
}
